import React, { useState, useEffect } from 'react';
import { collection, query, onSnapshot, addDoc, doc, updateDoc, deleteDoc, where, getDocs } from 'firebase/firestore';
import { db, auth } from '../../firebase';
import { FaCheck, FaTrash, FaPlus, FaChevronRight, FaUser, FaCalendar } from 'react-icons/fa';
import TaskmanagerHeader from '../taskmanagerHeader';
import '../styles/tasksMain.css';

function TasksMain({ toggleMenu }) {
  const [tasks, setTasks] = useState([]);
  const [projects, setProjects] = useState([]);
  const [editingTask, setEditingTask] = useState(null);
  const [expandedProjects, setExpandedProjects] = useState({});
  const [users, setUsers] = useState([]);

  // Hent alle opgaver uden projekt
  useEffect(() => {
    const q = query(
      collection(db, 'tasks'),
      where('projectId', '==', null)
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const tasksData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setTasks(tasksData);
    });

    return () => unsubscribe();
  }, []);

  // Hent alle projekter og deres opgaver
  useEffect(() => {
    const unsubscribe = onSnapshot(query(collection(db, 'projects')), async (snapshot) => {
      const projectsData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        tasks: [] // Vi fylder dette ud i næste trin
      }));

      // For hvert projekt, hent dets opgaver
      for (let project of projectsData) {
        const tasksQuery = query(
          collection(db, 'tasks'),
          where('projectId', '==', project.id)
        );
        
        const taskSnapshot = await getDocs(tasksQuery);
        project.tasks = taskSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
      }

      setProjects(projectsData);
    });

    return () => unsubscribe();
  }, []);

  // Tilføj useEffect for at hente brugere
  useEffect(() => {
    const q = query(collection(db, 'users'));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const usersData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setUsers(usersData);
    });

    return () => unsubscribe();
  }, []);

  const handleAddTask = async () => {
    try {
      const newTask = {
        name: '',
        completed: false,
        projectId: null,
        createdAt: new Date().toISOString(),
        createdBy: auth.currentUser.uid,
        updatedAt: new Date().toISOString(),
        responsible: '',
        deadline: null
      };

      const docRef = await addDoc(collection(db, 'tasks'), newTask);
      setEditingTask(docRef.id);
    } catch (error) {
      console.error('Fejl ved oprettelse af opgave:', error);
    }
  };

  const handleTaskNameChange = async (taskId, newName) => {
    try {
      const taskRef = doc(db, 'tasks', taskId);
      await updateDoc(taskRef, {
        name: newName,
        updatedAt: new Date().toISOString()
      });
      setEditingTask(null);
    } catch (error) {
      console.error('Fejl ved opdatering af opgave navn:', error);
    }
  };

  const toggleTaskStatus = async (taskId, completed) => {
    try {
      const taskRef = doc(db, 'tasks', taskId);
      await updateDoc(taskRef, {
        completed: !completed,
        updatedAt: new Date().toISOString()
      });
    } catch (error) {
      console.error('Fejl ved opdatering af opgave status:', error);
    }
  };

  const deleteTask = async (taskId) => {
    try {
      await deleteDoc(doc(db, 'tasks', taskId));
    } catch (error) {
      console.error('Fejl ved sletning af opgave:', error);
    }
  };

  const toggleProject = (projectId) => {
    setExpandedProjects(prev => ({
      ...prev,
      [projectId]: !prev[projectId]
    }));
  };

  const handleKeyPress = async (e, taskId, name) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      await handleTaskNameChange(taskId, name);
      handleAddTask();
    }
  };

  // Tilføj nye handlers
  const handleResponsibleChange = async (taskId, responsibleId) => {
    try {
      const taskRef = doc(db, 'tasks', taskId);
      await updateDoc(taskRef, {
        responsible: responsibleId,
        updatedAt: new Date().toISOString()
      });
    } catch (error) {
      console.error('Fejl ved opdatering af ansvarlig:', error);
    }
  };

  const handleDeadlineChange = async (taskId, deadline) => {
    try {
      const taskRef = doc(db, 'tasks', taskId);
      await updateDoc(taskRef, {
        deadline: deadline ? deadline : null,
        updatedAt: new Date().toISOString()
      });
    } catch (error) {
      console.error('Fejl ved opdatering af deadline:', error);
    }
  };

  const getDeadlineStatus = (deadline) => {
    if (!deadline) return null;
    
    const today = new Date();
    const deadlineDate = new Date(deadline);
    const daysUntilDeadline = Math.ceil((deadlineDate - today) / (1000 * 60 * 60 * 24));
    
    if (daysUntilDeadline < 0) {
      return 'overdue';
    } else if (daysUntilDeadline <= 3) {
      return 'warning';
    }
    return null;
  };

  const formatDeadline = (deadline) => {
    if (!deadline) return '';
    const date = new Date(deadline);
    return date.toLocaleDateString('da-DK');
  };

  return (
    <div className="tasks-main">
      <TaskmanagerHeader toggleMenu={toggleMenu} />
      
      <div className="tasks-content">
        <div className="tasks-table-container">
          <table className="tasks-table">
            <thead>
              <tr>
                <th style={{ width: '40px' }}></th>
                <th>Opgave</th>
                <th style={{ width: '200px' }}>Ansvarlig</th>
                <th style={{ width: '150px' }}>Deadline</th>
                <th style={{ width: '80px' }}>Handling</th>
              </tr>
            </thead>
            <tbody>
              {/* Almindelige opgaver */}
              {tasks.map(task => {
                const deadlineStatus = getDeadlineStatus(task.deadline);
                return (
                  <tr key={task.id} className={`task-row ${task.completed ? 'completed' : ''} ${deadlineStatus || ''}`}>
                    <td>
                      <button
                        className={`status-toggle ${task.completed ? 'completed' : ''}`}
                        onClick={() => toggleTaskStatus(task.id, task.completed)}
                      >
                        <FaCheck />
                      </button>
                    </td>
                    <td>
                      {editingTask === task.id ? (
                        <input
                          type="text"
                          className="task-name-input"
                          defaultValue={task.name}
                          autoFocus
                          onBlur={(e) => handleTaskNameChange(task.id, e.target.value)}
                          onKeyDown={(e) => handleKeyPress(e, task.id, e.target.value)}
                        />
                      ) : (
                        <span 
                          className="task-name"
                          onClick={() => setEditingTask(task.id)}
                        >
                          {task.name || 'Klik for at tilføje navn'}
                        </span>
                      )}
                    </td>
                    <td>
                      <select
                        className="responsible-select"
                        value={task.responsible || ''}
                        onChange={(e) => handleResponsibleChange(task.id, e.target.value)}
                      >
                        <option value="">Vælg ansvarlig...</option>
                        {users.map(user => (
                          <option key={user.id} value={user.id}>
                            {user.name || user.email}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td>
                      <div className={`deadline-cell ${deadlineStatus ? `deadline-${deadlineStatus}` : ''}`}>
                        {deadlineStatus && <div className="deadline-status" />}
                        <input
                          type="date"
                          className={`deadline-input ${deadlineStatus ? `deadline-${deadlineStatus}` : ''}`}
                          value={task.deadline || ''}
                          onChange={(e) => handleDeadlineChange(task.id, e.target.value)}
                        />
                      </div>
                    </td>
                    <td>
                      <button
                        className="delete-task"
                        onClick={() => deleteTask(task.id)}
                      >
                        <FaTrash />
                      </button>
                    </td>
                  </tr>
                );
              })}

              {/* Projekter og deres opgaver */}
              {projects.map(project => (
                <React.Fragment key={project.id}>
                  <tr className="project-row" onClick={() => toggleProject(project.id)}>
                    <td colSpan="5">
                      <div className={`project-toggle ${expandedProjects[project.id] ? 'expanded' : ''}`}>
                        <FaChevronRight />
                        <span>{project.name}</span>
                      </div>
                    </td>
                  </tr>
                  
                  {expandedProjects[project.id] && project.tasks.map(task => {
                    const deadlineStatus = getDeadlineStatus(task.deadline);
                    return (
                      <tr key={task.id} className={`task-row ${task.completed ? 'completed' : ''} ${deadlineStatus || ''}`}>
                        <td>
                          <button
                            className={`status-toggle ${task.completed ? 'completed' : ''}`}
                            onClick={() => toggleTaskStatus(task.id, task.completed)}
                          >
                            <FaCheck />
                          </button>
                        </td>
                        <td>
                          {editingTask === task.id ? (
                            <input
                              type="text"
                              className="task-name-input"
                              defaultValue={task.name}
                              autoFocus
                              onBlur={(e) => handleTaskNameChange(task.id, e.target.value)}
                              onKeyDown={(e) => handleKeyPress(e, task.id, e.target.value)}
                            />
                          ) : (
                            <span 
                              className="task-name"
                              onClick={() => setEditingTask(task.id)}
                            >
                              {task.name || 'Klik for at tilføje navn'}
                            </span>
                          )}
                        </td>
                        <td>
                          <select
                            className="responsible-select"
                            value={task.responsible || ''}
                            onChange={(e) => handleResponsibleChange(task.id, e.target.value)}
                          >
                            <option value="">Vælg ansvarlig...</option>
                            {users.map(user => (
                              <option key={user.id} value={user.id}>
                                {user.name || user.email}
                              </option>
                            ))}
                          </select>
                        </td>
                        <td>
                          <div className={`deadline-cell ${deadlineStatus ? `deadline-${deadlineStatus}` : ''}`}>
                            {deadlineStatus && <div className="deadline-status" />}
                            <input
                              type="date"
                              className={`deadline-input ${deadlineStatus ? `deadline-${deadlineStatus}` : ''}`}
                              value={task.deadline || ''}
                              onChange={(e) => handleDeadlineChange(task.id, e.target.value)}
                            />
                          </div>
                        </td>
                        <td>
                          <button
                            className="delete-task"
                            onClick={() => deleteTask(task.id)}
                          >
                            <FaTrash />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </React.Fragment>
              ))}

              {/* Tilføj opgave knap */}
              <tr className="add-task-row">
                <td colSpan="5">
                  <button onClick={handleAddTask} className="add-task-button">
                    <FaPlus />
                    <span>Tilføj opgave</span>
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export default TasksMain;
